import { Box, Grid, IconButton, Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useWebhooks } from "api/webhooks";
import { EmptyState } from "components/EmptyState/EmptyState";
import { FormButtons } from "components/FormControls/FormButtons";
import { FormSection } from "components/FormControls/FormSection";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as FilePlus03Icon } from "components/Icons/file-plus-03.svg";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { FieldArray, Form, Formik, FormikProps } from "formik";
import { useMessage } from "hooks/intl";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { BackendWebhookModel, WebhookHeader } from "types/webhooks";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { useStyles } from "./WebhookForm.styles";

export function WebhookForm({
  editMode,
  initialValues: editedValues,
  "data-test-id": dataTestId,
}: {
  editMode?: boolean;
  initialValues?: BackendWebhookModel;
  "data-test-id"?: string;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const { addWebhook, editWebhook, useWebhookEventsList } = useWebhooks();

  const { data: webhookEventsList } = useWebhookEventsList();

  const initialValues: BackendWebhookModel =
    editMode && editedValues
      ? {
          createdAt: editedValues.createdAt,
          eventName: editedValues.eventName,
          url: editedValues.url,
          webhookSubscriptionId: editedValues.webhookSubscriptionId,
          headers: editedValues.headers,
        }
      : {
          eventName: "",
          url: "",
          headers: [],
        };

  return (
    <div className={classes.formContainer}>
      <Formik
        enableReinitialize
        initialStatus={{}}
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});
          try {
            if (editMode && editedValues) {
              await editWebhook(editedValues?.webhookSubscriptionId!, {
                ...values,
                createdAt: undefined,
                eventName: undefined,
                webhookSubscriptionId: undefined,
              });
              navigate(links.webhookList);
              notify(message({ id: "webhookUpdated" }));
            } else {
              await addWebhook(values);
              navigate(links.webhookList);
              notify(message({ id: "webhookAdded" }));
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({
          handleSubmit,
          isSubmitting,
          values,
          status,
          setFieldValue,
          dirty,
          initialValues,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <FormSection
                title={message({ id: "webhookConfiguration" })}
                sectionError={status?.[""]}
              >
                <Grid container spacing={12}>
                  <Grid item xs={12}>
                    <SelectInput
                      data-test-id={`${dataTestId}-webhook-name-select-input`}
                      disabled={editMode}
                      name="eventName"
                      items={
                        webhookEventsList?.items
                          ? _.sortBy(webhookEventsList.items).map((event) => ({
                              id: event,
                              label: message({ id: event }),
                            }))
                          : []
                      }
                      label={
                        <RequiredFieldLabel
                          text={message({ id: "webhookName" })}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      label={
                        <RequiredFieldLabel
                          text={message({ id: "webhookUrl" })}
                        />
                      }
                      name="url"
                      data-test-id={`${dataTestId}-webhook-url-input`}
                      width="100%"
                    />
                  </Grid>
                </Grid>
              </FormSection>
              <Typography className={classes.header}>
                {message({ id: "headers" })}
              </Typography>
              <OLPaper className={classes.olPaper}>
                <FieldArray
                  name="headers"
                  render={({
                    remove,
                    form,
                  }: {
                    remove: (index: number) => void;
                    form: FormikProps<BackendWebhookModel>;
                  }) => {
                    const { headers } = form.values;
                    const areHeadersAdded = headers && headers.length > 0;

                    return (
                      <>
                        {areHeadersAdded ? (
                          <>
                            {headers.map((header: WebhookHeader, index) => (
                              <Grid container columnSpacing={8} key={index}>
                                <Grid item xs>
                                  <TextInput
                                    label={
                                      <RequiredFieldLabel
                                        text={message({ id: "header" })}
                                      />
                                    }
                                    name={`headers[${index}].headerName`}
                                    value={header.headerName}
                                    data-test-id={`${dataTestId}-${index}-webhook-header-name-input`}
                                  />
                                </Grid>
                                <Grid item xs>
                                  <TextInput
                                    label={
                                      <RequiredFieldLabel
                                        text={message({ id: "value" })}
                                      />
                                    }
                                    name={`headers[${index}].headerValue`}
                                    value={header.headerValue}
                                    data-test-id={`${dataTestId}-${index}-webhook-header-value-input`}
                                  />
                                </Grid>
                                <Grid item xs="auto">
                                  <IconButton
                                    color="inherit"
                                    onClick={() => remove(index)}
                                    className={classes.headerRemoveButton}
                                    data-test-id={`${dataTestId}-${index}-webhook-header-remove-button`}
                                  >
                                    <XCloseIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            ))}
                            <OLButton
                              className={classes.addHeaderButton}
                              variant="outlined"
                              onClick={() =>
                                setFieldValue(`headers`, [
                                  ...values.headers,
                                  { headerName: "", headerValue: "" },
                                ])
                              }
                              startIcon={<PlusIcon />}
                              data-test-id={`${dataTestId}-add-webhook-header-button`}
                            >
                              {message({ id: "addHeaderName&Value" })}
                            </OLButton>
                          </>
                        ) : (
                          <EmptyState
                            icon={<FilePlus03Icon className={classes.icon} />}
                            title={message({
                              id: "addHeaderNameAndValue",
                            })}
                            description={message({ id: "headersInfo" })}
                            button={
                              <OLButton
                                variant="contained"
                                onClick={() =>
                                  setFieldValue(`headers`, [
                                    ...values.headers,
                                    { headerName: "", headerValue: "" },
                                  ])
                                }
                                data-test-id={`${dataTestId}-add-webhook-header-button`}
                              >
                                {message({ id: "addHeaderName&Value" })}
                              </OLButton>
                            }
                          />
                        )}
                      </>
                    );
                  }}
                />
              </OLPaper>

              {!isSubmitting && (
                <LeaveSiteGuard
                  warnBeforeRefresh
                  isDirty={dirty}
                  initialValues={initialValues}
                  currentValues={values}
                />
              )}
              <Box mt={12} />
              <FormButtons
                disableSubmit={isSubmitting}
                onCancelClick={() => navigate(links.webhookList)}
                onSubmitClick={() => handleSubmit()}
                {...(!editMode && { submitMessage: "addWebhook" })}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
